import React from 'react';
import {
  Button,
} from 'react-bootstrap';

import exact from 'prop-types-exact';
import PropTypes from 'prop-types';

import ReactTable from 'react-table';
import { getTicketLink } from '../../ticketViewer/links';

import {
  defaultTableFiltering,
  startsWithTableFiltering,
  openInNewTab,
  toDollarFormat,
} from '../../../utils';

/* eslint-disable react/no-unused-prop-types */
const propTypes = exact({
  allTickets: PropTypes.array.isRequired,
  pageInfo: PropTypes.object,
  setPageSizeAction: PropTypes.func.isRequired,
  setNextCursorAction: PropTypes.func.isRequired,
  setPrevCursorAction: PropTypes.func.isRequired,
  currentPageSize: PropTypes.number.isRequired,
  jobTicketNumber: PropTypes.string,
  departmentId: PropTypes.number,
  districtId: PropTypes.number,

  setTableOrderAction: PropTypes.func.isRequired,

  nextCursor: PropTypes.string,
  prevCursor: PropTypes.string,
  last: PropTypes.number,
  first: PropTypes.number,

  rowCount: PropTypes.number,
  currentPage: PropTypes.number,
  pages: PropTypes.number,
  operatorId: PropTypes.number,
  leaseId: PropTypes.number,
  sorted: PropTypes.array.isRequired,

  loading: PropTypes.bool.isRequired,
});
/* eslint-enable react/no-unused-prop-types */

const TicketTable = (props) => {
  const {
    allTickets,
    setPageSizeAction,
    currentPageSize,
    setNextCursorAction,
    setPrevCursorAction,
    setTableOrderAction,
    pages,
    currentPage,
    loading,
    pageInfo,
    sorted,
  } = props;

  const handleSelectTicketObject = (selectedObject) => {
    const ticketId = selectedObject.ticket_number.ticket.id;
    const jobId = selectedObject.job.id;
    const url = getTicketLink(ticketId, jobId);
    openInNewTab(url);
  };

  const handleNext = () => {
    if (!loading) {
      const { hasNextPage, endCursor } = pageInfo;
      if (hasNextPage) {
        setNextCursorAction(endCursor);
      }
    }
  };

  const handlePrevious = () => {
    if (!loading) {
      const { hasPreviousPage, startCursor } = pageInfo;
      if (hasPreviousPage) {
        setPrevCursorAction(startCursor);
      }
    }
  };

  const noData = () => {
    if (loading) {
      return null;
    }
    return <div className="rt-noData">No rows found</div>;
  };

  return (
    <div>
      <ReactTable
        data={allTickets}
        loading={loading}
        filterable
        manual
        NoDataComponent={noData}
        showPageJump={false}
        sorted={sorted}
        onSortedChange={(newSorted, column, shiftkey) => {
          const sortables = [];
          newSorted.forEach((sort, index) => {
            sortables.push({
              id: sort.id,
              order: index,
              desc: sort.desc,
            });
          });
          /* FIXME: When adding more than date sorting, update logic here */
          /* NOTE: desc field is a bool */
          setTableOrderAction(sortables);
        }}
        onPageSizeChange={(pageSize) => {
          setPageSizeAction(pageSize);
        }}
        defaultFilterMethod={(filter, row) => {
          return defaultTableFiltering(filter, row);
        }}
        PreviousComponent={() => {
          return (
            <React.Fragment>
              <Button onClick={handlePrevious}>Prev</Button>
            </React.Fragment>
          );
        }}
        NextComponent={() => {
          return (
            <React.Fragment>
              <Button onClick={handleNext}>Next</Button>
            </React.Fragment>
          );
        }}
        columns={[
          {
            Header: 'Date',
            id: 'date',
            accessor: (e) => { return e.date.display; },
            filterable: false,
          },
          {
            Header: 'Ticket Number',
            id: 'number',
            accessor: (e) => { return e.ticket_number.number; },
            filterable: false,
          },
          {
            Header: 'Net Revenue',
            id: 'billing_revenue',
            minWidth: 115,
            accessor: (e) => { return e.billing_revenue; },
            sortable: false,
            filterable: false,
            Cell: (row) => {
              const { billing_revenue: billingRevenue } = row.original;
              return (
                <div>
                  {toDollarFormat(billingRevenue)}
                </div>
              );
            },
          },
          {
            Header: 'Ticket Status',
            id: 'ticket_state',
            accessor: (e) => { return e.ticket_number.ticket.state; },
            sortable: false,
            filterable: false,
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return startsWithTableFiltering(customFilterObj, row);
            },
          },
          {
            Header: 'Job Number',
            id: 'job_number',
            accessor: (e) => { return e.job.job_number; },
            filterable: false,
          },
          {
            Header: 'Job Status',
            id: 'job_state',
            accessor: (e) => { return e.job.state; },
            sortable: false,
            filterable: false,
            filterMethod: (filter, row) => {
              const customFilterObj = {
                id: filter.id,
                value: filter.value,
              };
              return startsWithTableFiltering(customFilterObj, row);
            },
          },
          {
            Header: 'Department',
            id: 'department',
            accessor: (e) => { return e.department.name; },
            sortable: false,
            filterable: false,
            Cell: (row) => {
              return (
                <div>
                  {row.original.department.name}
                </div>
              );
            },
          },
          {
            Header: 'District',
            id: 'district',
            accessor: (e) => { return e.district.name; },
            sortable: false,
            filterable: false,
            Cell: (row) => {
              return (
                <div>
                  { row.original.district.name }
                </div>
              );
            },
          },
          {
            Header: 'Operator',
            id: 'operator',
            accessor: (e) => { return e.well.lease.operator.name; },
            sortable: false,
            filterable: false,
          },
          {
            Header: 'Lease',
            id: 'lease',
            accessor: (e) => { return e.well.lease.name; },
            sortable: false,
            filterable: false,
          },
          {
            Header: 'Well',
            id: 'well',
            accessor: (e) => { return e.well.name; },
            sortable: false,
            filterable: false,
          },
          {
            Header: 'Actions',
            id: 'actions',
            filterable: false,
            sortable: false,
            Cell: (row) => {
              return (
                <div>
                  <Button
                    bsStyle="info"
                    bsSize="xsmall"
                    onClick={() => { handleSelectTicketObject(row.original); }}
                  >View
                  </Button>
                </div>

              );
            },
          },
        ]}
        defaultPageSize={currentPageSize}
        pages={pages}
        page={currentPage}
        className="-striped -highlight"
      />
    </div>
  );
};

TicketTable.propTypes = propTypes;
export default TicketTable;
